<template>
  <v-timeline-item
    v-if="conversion"
    dot-color="leadActivity"
    icon="mdi-home-search"
    fill-dot
  >
    <v-expansion-panels
      v-if="conversion && property"
      class="expansion-panels"
      variant="accordion"
      flat
    >
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row
            no-gutters
            align="center"
            class="flex-row justify-center d-flex"
            :class="{ 'flex-column': $vuetify.display.smAndDown }"
          >
            <v-col>
              <div class="mb-2 text-subtitle-1">
                {{ $t('property-request.title-timeline') }}
              </div>
              <div class="text-grey">
                {{ propertyTypeLabel }} • {{ $t('property-request.conversion.labels.in') }}
                {{ property.location.description }} + {{ property.location.radius }}km •
                {{ formatPrice(property.minPrice) }}
                {{ $t('property-request.conversion.labels.until') }}
                {{ formatPrice(property.maxPrice) }}
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row>
            <v-col md="4">
              <v-list density="compact">
                <v-list-item class="px-0">
                  <v-list-item-title>
                    {{ $t('property-request.conversion.labels.contact') }}:
                  </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ personalData.firstname }} {{ personalData.lastname }}
                  </div>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ personalData.phone }}
                  </div>
                </v-list-item>
                <TimelineListItem
                  :label="$t('labels.source')"
                  :value="source"
                />
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.living-situation')"
                  :value="currentLivingSituation"
                />
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.financing')"
                  :value="financing"
                />
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.timeline')"
                  :input-class="isPropertyRequestUrgent ? 'text-red' : 'text-grey'"
                  :value="availableStartDate"
                />
                <TimelineListItem
                  :label="$t('labels.utm-source')"
                  :value="utmSource || $t('labels.none')"
                />
                <TimelineListItem
                  v-if="utmMedium"
                  :label="$t('labels.utm-medium')"
                  :value="utmMedium"
                />
              </v-list>
            </v-col>
            <v-col md="4">
              <v-list density="compact">
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.looks-for')"
                  :value="propertyTypeLabel"
                />
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.location')"
                  :value="residentialArea"
                />
                <TimelineListItem :label="$t('property-request.conversion.labels.search-radius')">
                  {{ property.location.description }} + {{ property.location.radius }}km
                </TimelineListItem>
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.important-for-contact')"
                >
                  <ul>
                    <li
                      v-for="surrounding of surroundings"
                      :key="surrounding"
                    >
                      {{ $t(surrounding) }}
                    </li>
                  </ul>
                </TimelineListItem>
                <TimelineListItem
                  v-if="utmCampaign"
                  :label="$t('labels.utm-campaign')"
                  :value="utmCampaign"
                />
                <TimelineListItem
                  v-if="utmContent"
                  :label="$t('labels.utm-content')"
                  :value="utmContent"
                />
              </v-list>
            </v-col>
            <v-col md="4">
              <v-list density="compact">
                <TimelineListItem :label="$t('property-request.conversion.labels.rooms')">
                  {{ $t('property-request.conversion.labels.minimum') }}
                  {{ property.minNumberOfRooms }}
                </TimelineListItem>
                <TimelineListItem :label="$t('property-request.conversion.labels.bathrooms')">
                  {{ $t('property-request.conversion.labels.minimum') }}
                  {{ property.minNumberOfBathrooms }}
                </TimelineListItem>
                <TimelineListItem :label="$t('property-request.conversion.labels.parking-spaces')">
                  {{ $t('property-request.conversion.labels.minimum') }}
                  {{ property.minNumberOfParkingSpaces }}
                </TimelineListItem>
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.outdoor-area')"
                  :value="outdoorArea"
                />
                <TimelineListItem
                  :label="$t('property-request.conversion.labels.accessibility')"
                  :value="accessibility"
                />
                <TimelineListItem
                  v-if="utmTerm"
                  :label="$t('labels.utm-term')"
                  :value="utmTerm"
                />
              </v-list>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-timeline-item>
</template>

<script>
import { conversionSource } from '@/lib/conversionSource'
import AvailableStartDate from '@/lib/enums/propertyRequest/AvailableStartDate'
import {
  AccessibilityLabels,
  AvailableStartDateLabels,
  FinancingLabels,
  LivingSituationLabels,
  OutdoorAreaLabels,
  ResidentialAreaLabels,
  SurroundingsLabels,
} from '@/lib/propertyRequest'
import { propertySubTypes, propertyTypes } from '@/lib/propertyTypes'
import { formatSpaceUnit } from '@/lib/spaceUnit'
import currencyMixin from '@/mixins/currency'

import CONVERSION from '../queries/Conversion.gql'
import TimelineListItem from './TimelineListItem.vue'

export default {
  components: { TimelineListItem },
  mixins: [currencyMixin],
  props: {
    data: { type: Object, default: null },
    leadStatus: { type: String, default: '' },
  },
  data() {
    return {
      conversion: {
        personalData: {},
        propertyRequest: {},
      },
    }
  },
  computed: {
    personalData() {
      return this.conversion?.personalData
    },
    propertyRequest() {
      return this.conversion?.propertyRequest
    },
    property() {
      return this.conversion?.propertyRequest?.property
    },
    propertyTypeLabel() {
      if (this.property?.type) {
        return `${this.$t(propertyTypes[this.property.type])} ${this.property?.subType ? this.$t(propertySubTypes[this.property.subType]) : this.$t('valuation.labels.flexible')}`
      }
      return this.$t('valuation.labels.flexible')
    },
    source() {
      return this.conversion?.source || conversionSource[this.conversion?.origin] || ''
    },
    accessibility() {
      return this.property?.accessibility
        ? this.$t(AccessibilityLabels[this.property?.accessibility])
        : ''
    },
    availableStartDate() {
      return this.$t(AvailableStartDateLabels[this.propertyRequest?.availableStartDate])
    },
    financing() {
      if (this.propertyRequest?.financing)
        return this.$t(FinancingLabels[this.propertyRequest.financing])
      return this.$t('property-request.conversion.labels.no-answer')
    },
    currentLivingSituation() {
      if (this.propertyRequest?.currentLivingSituation)
        return this.$t(LivingSituationLabels[this.propertyRequest.currentLivingSituation])
      return this.$t('property-request.conversion.labels.no-answer')
    },
    outdoorArea() {
      if (this.property?.outdoorArea) return this.$t(OutdoorAreaLabels[this.property.outdoorArea])
      return this.$t('valuation.labels.flexible')
    },
    residentialArea() {
      if (this.property?.residentialArea)
        return this.$t(ResidentialAreaLabels[this.property.residentialArea])
      return this.$t('valuation.labels.flexible')
    },
    surroundings() {
      return this.property.surroundings.map((key) => SurroundingsLabels[key])
    },
    isPropertyRequestUrgent() {
      return (
        this.propertyRequest.availableStartDate === AvailableStartDate.EARLIEST &&
        this.leadStatus === 'new'
      )
    },
    utmSource() {
      return this.conversion?.utmParams?.source || ''
    },
    utmMedium() {
      return this.conversion?.utmParams?.medium || ''
    },
    utmCampaign() {
      return this.conversion?.utmParams?.campaign || ''
    },
    utmContent() {
      return this.conversion?.utmParams?.content || ''
    },
    utmTerm() {
      return this.conversion?.utmParams?.term || ''
    },
  },
  methods: {
    formatSpaceUnit,
  },
  apollo: {
    conversion: {
      query: CONVERSION,
      variables() {
        return {
          conversionId: this.data.details[this.data.type].conversionId,
        }
      },
    },
  },
}
</script>

<style scoped>
.expansion-panels {
  margin: -24px -24px 0 -24px;
}
</style>
